import React, { createContext, useContext } from 'react';
import * as topicsApi from '../api/topics';
import * as materialsApi from '../api/materials';
import * as sessionsApi from '../api/sessions';
import { toUiMaterial } from '../api/adapters';
import { useAuth } from './AuthContext';

// Shared topics state. Backed by the API: the admin Classroom creates, edits and
// deletes topics and their contents, and the student Classroom + Report read
// them back.
//
// The mutators keep the signatures the pages already call, so AdminClassroom and
// ClassroomPage didn't change when this stopped being an in-memory array. Each
// one writes to the server first and only then updates local state, so the UI
// never shows something the server rejected.
/**
 * How often the live session is re-checked, in ms.
 *
 * Fifteen seconds: a class starting is not a sub-second event, and the endpoint is
 * a single indexed row, but a student staring at the classroom page should not sit
 * in front of a stale "no live class" for long. Combined with the
 * refresh-on-focus below, the perceived delay is usually zero.
 */
const LIVE_POLL_MS = 15000;

const TopicsContext = createContext(null);

export function TopicsProvider({ children }) {
  const { user, restoring } = useAuth();
  const [topics, setTopics] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);


  // Topics are scoped to the caller's group by the server, so they can only be
  // fetched once there is a session. Refetch when the account changes, because a
  // different user may see a different group.
  //
  // Keyed on the id, not the `user` object: depending on the object means any
  // provider that hands back a fresh one each render re-creates this callback,
  // re-runs the effect, and loops fetching forever.
  const userId = user ? user.id : null;

  /**
   * The currently live session, from the SERVER — `{ id, topicId, … }` or null.
   *
   * This used to be a bare `useState` holding a topic id, with `startLive` doing
   * nothing but `setLive(topicId)`. Nothing was ever sent anywhere: no session was
   * created, no student saw one, no attendance was recorded, and a refresh threw
   * the "live" state away. The endpoints exist and are called now.
   *
   * `live` stays the SESSION rather than just a topic id, because ending one and
   * reading its attendance both need `sessionId`, which only the server can mint.
   */
  const [live, setLive] = React.useState(null);
  const [liveBusy, setLiveBusy] = React.useState(false);
  // Guards against overlapping polls: a slow response must not have a second
  // request stacked behind it, and an older reply must not land after a newer one.
  const livePolling = React.useRef(false);

  /**
   * Restores what is already live, for both roles: an admin who refreshes
   * mid-class must not lose the Stop button, and a student needs to be told their
   * class is running. getActiveSession resolves null rather than throwing when
   * nothing is live, which is the ordinary case.
   *
   * The state is only REPLACED when the session actually changed. `toUiSession`
   * builds a fresh object every call, so assigning it unconditionally would hand
   * every consumer a new context value on each poll — re-running their effects and
   * re-rendering the whole classroom every few seconds for no reason.
   */
  const refreshLive = React.useCallback(async () => {
    if (!userId) { setLive(null); return null; }
    if (livePolling.current) return null;
    livePolling.current = true;
    try {
      const current = await sessionsApi.getActiveSession();
      setLive((prev) => {
        const same = (prev?.id ?? null) === (current?.id ?? null)
          && (prev?.finished ?? null) === (current?.finished ?? null);
        return same ? prev : current;
      });
      return current;
    } catch (e) {
      // A failure here must not break the classroom pages, which are useful
      // whether or not anything is live — and must not flap an existing session
      // off the screen over one dropped request.
      return null;
    } finally {
      livePolling.current = false;
    }
  }, [userId]);

  /**
   * Keeps the live state fresh without a page refresh.
   *
   * SSE is the mechanism the backend was built for — `notifyStudents` already
   * emits a `session_update` event when a session starts. It is not usable from
   * here: `/student/studentSSEConnection` is behind `studentProtect`, which reads
   * the token from the Authorization header only (its `?token=` branch sits after
   * an early `return` and is unreachable), and a native EventSource cannot send
   * headers. So this polls instead, which needs no server change.
   *
   * Deliberately quiet: it skips a tick while the tab is hidden, and refreshes
   * immediately on focus — which is what makes it feel instant, since a student
   * coming back to the tab sees the class already live rather than waiting out the
   * interval.
   */
  React.useEffect(() => {
    if (!userId) return undefined;

    const tick = () => {
      // Nothing to learn from the server mid-write, and the write already sets
      // the state itself.
      if (liveBusy || document.hidden) return;
      refreshLive();
    };
    const onFocus = () => { if (!document.hidden && !liveBusy) refreshLive(); };

    const id = setInterval(tick, LIVE_POLL_MS);
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [userId, liveBusy, refreshLive]);

  const startLive = async (topicId) => {
    setLiveBusy(true);
    try {
      const started = await sessionsApi.startSession(topicId);
      /**
       * startSession's response carries the new row, but `refreshLive` is what
       * settles the state: the response shape is the controller's own hand-built
       * object rather than the model, so re-reading gives the same shape every
       * other path produces — and confirms the server really considers it live.
       */
      setLive(started);
      return await refreshLive();
    } finally {
      setLiveBusy(false);
    }
  };

  const stopLive = async () => {
    setLiveBusy(true);
    try {
      await sessionsApi.endSession();
      setLive(null);
    } finally {
      setLiveBusy(false);
    }
  };

  // Marks the caller present in whatever is live. Students only in practice —
  // the server answers an admin with "Admin entering session." and records
  // nothing.
  const attendLive = () => sessionsApi.attendSession();
  const load = React.useCallback(async () => {
    if (!userId) { setTopics([]); return; }
    setLoading(true);
    setError(null);
    try {
      setTopics(await topicsApi.listTopics());
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  React.useEffect(() => {
    if (restoring) return;
    load();
    refreshLive();
  }, [load, refreshLive, restoring]);

  /**
   * Loads one topic's full contents (materials, and the raw quizzes and
   * assignments) and merges them into the cached list.
   *
   * The list endpoint returns topics without their contents, so opening a topic
   * needs this. get_topic_by_id already joins materials, so it is one request.
   */
  const loadTopic = React.useCallback(async (topicId) => {
    const full = await topicsApi.getTopic(topicId);
    setTopics((prev) => prev.map((t) => (t.id === String(topicId) ? {
      ...t,
      ...full,
      // get_topic_by_id computes none of the card's totals, so its nulls must not
      // erase what listTopics worked out — the card would drop to whatever was
      // loaded the moment a topic was opened and returned from. The contents are
      // in hand now, so they are the better count.
      materialsCount: full.materials.length,
      videosCount: full.videos.length,
      liveSessionsCount: full.liveSessionsCount ?? t.liveSessionsCount,
    } : t)));
    return full;
  }, []);

  const addTopic = async (data) => {
    const created = await topicsApi.createTopic(data);
    setTopics((prev) => [...prev, created]);
    return created;
  };

  const updateTopic = async (id, patchFields) => {
    const updated = await topicsApi.updateTopic(id, patchFields);
    // The response omits the nested contents, so keep what we already have.
    setTopics((prev) => prev.map((t) => (t.id === String(id)
      ? { ...t, ...updated, materials: t.materials, videos: t.videos }
      : t)));
    return updated;
  };

  const removeTopic = async (id) => {
    await topicsApi.deleteTopic(id);
    setTopics((prev) => prev.filter((t) => t.id !== String(id)));
  };

  /**
   * Adds a material to a topic. `kind` is 'materials' (PDFs) or 'videos'
   * (links) to match the old signature, but the server doesn't store the
   * distinction — it's derived from the URL — so the created row is re-split by
   * the adapter rather than trusted to land in the requested bucket.
   */
  // Files a material row into the bucket its URL says it belongs to, removing it
  // from the other one first. Editing a PDF into a link (or back) has to move it,
  // and a stale copy in the old bucket would show the item twice.
  // Nudges a card's count, leaving null alone — null means "not told", and
  // inventing a 1 from it would be worse than showing nothing.
  const bump = (value, delta) => (value == null ? null : Math.max(0, value + delta));

  const fileIntoBucket = (topicId, row) => setTopics((prev) => prev.map((t) => {
    if (t.id !== String(topicId)) return t;
    /**
     * The counts on the card come from the list endpoint, so they have to move
     * with the contents or the card keeps the figures it was loaded with.
     *
     * Derived from whether this row was already filed, which covers both cases in
     * one rule: a new row adds to its bucket, and an EDIT that turns a PDF into a
     * link (or back) moves one across.
     */
    const wasPdf = t.materials.some((x) => x.id === row.id);
    const wasUrl = t.videos.some((x) => x.id === row.id);
    const isPdf = row.kind === 'pdf';
    const next = {
      ...t,
      materials: t.materials.filter((x) => x.id !== row.id),
      videos: t.videos.filter((x) => x.id !== row.id),
      materialsCount: bump(t.materialsCount, (isPdf ? 1 : 0) - (wasPdf ? 1 : 0)),
      videosCount: bump(t.videosCount, (isPdf ? 0 : 1) - (wasUrl ? 1 : 0)),
    };
    const bucket = isPdf ? 'materials' : 'videos';
    next[bucket] = [...next[bucket], row];
    return next;
  }));

  const addItem = async (topicId, kind, item) => {
    const created = await materialsApi.createMaterial({ ...item, topicId });
    fileIntoBucket(topicId, created);
    return created;
  };

  const updateItem = async (topicId, kind, itemId, patchFields) => {
    // topicId is required by the update route's validation, not just for our
    // own bookkeeping — see materials.updateMaterial.
    await materialsApi.updateMaterial(itemId, { ...patchFields, topicId });

    // The response carries no row, so it is rebuilt from the existing one plus
    // the patch, then re-derived through the adapter so `kind` reflects the new
    // document rather than the bucket the caller happened to edit from.
    const existing = (topics.find((t) => t.id === String(topicId)) || { materials: [], videos: [] });
    const previous = [...existing.materials, ...existing.videos].find((x) => x.id === String(itemId));
    const merged = { ...previous, ...patchFields };
    const row = toUiMaterial({
      materialId: itemId,
      title: merged.title,
      description: merged.pages ?? merged.n ?? '',
      document: merged.file || merged.url || '',
      topicId,
    });
    fileIntoBucket(topicId, row);
    return row;
  };

  const removeItem = async (topicId, kind, itemId) => {
    await materialsApi.deleteMaterial(itemId);
    // Cleared from both buckets rather than the one the caller named: a PDF
    // edited into a link lives in `videos` now, and trusting `kind` would leave
    // the deleted row on screen.
    setTopics((prev) => prev.map((t) => {
      if (t.id !== String(topicId)) return t;
      // Which count to decrement is decided by where the row actually was, for
      // the same reason the delete itself clears both buckets.
      const wasPdf = t.materials.some((x) => x.id === String(itemId));
      const wasUrl = t.videos.some((x) => x.id === String(itemId));
      return {
        ...t,
        materials: t.materials.filter((x) => x.id !== String(itemId)),
        videos: t.videos.filter((x) => x.id !== String(itemId)),
        materialsCount: wasPdf ? bump(t.materialsCount, -1) : t.materialsCount,
        videosCount: wasUrl ? bump(t.videosCount, -1) : t.videosCount,
      };
    }));
  };

  const value = React.useMemo(() => ({
    topics,
    loading,
    error,
    reload: load,
    loadTopic,
    addTopic,
    updateTopic,
    removeTopic,
    addItem,
    updateItem,
    removeItem,
    live,
    liveBusy,
    startLive,
    stopLive,
    attendLive,
    refreshLive,
  }), [topics, loading, error, load, loadTopic, live, liveBusy, refreshLive]); // eslint-disable-line react-hooks/exhaustive-deps

  return <TopicsContext.Provider value={value}>{children}</TopicsContext.Provider>;
}

export const useTopics = () => useContext(TopicsContext);
